<template>
  <div class="rp-hero" qid="recurring-hero">
    <div class="grow">
      <div class="rp-hero__crumb">
        <router-link to="/projects?view=list">{{ $t('projects.management.title') }}</router-link>
        <font-awesome-icon icon="chevron-right"/>
        {{ $t('projects.management.recurring') }}
      </div>
      <h1 data-qid="p-view-page-title">{{ project.title }}</h1>
      <div class="rp-hero__chips">
        <span class="rp-chip"><font-awesome-icon :icon="meta.icon"/>{{ $t('projects.management.types.' + project.type) }}</span>
        <span class="rp-chip"><font-awesome-icon icon="repeat"/>{{ scheduleText }}</span>
        <span class="rp-chip" :class="{'rp-chip--on': project.auto_create}" v-if="project.scheduled">
          <span class="rp-switch" :class="{'on': project.auto_create}"></span>
          {{ project.auto_create ? $t('projects.recurring.created_automatically') : $t('projects.recurring.started_by_hand') }}
        </span>
        <span class="rp-chip" v-if="project.scope">{{ $t('projects.launcher.scopes.' + project.scope) }}</span>
      </div>
      <div class="rp-hero__actions">
        <span class="rp-hero__btn"
              :class="{'rp-hero__btn--due': project.run_due}"
              v-if="canRun"
              @click="$emit('run')"
              qid="recurring-run">
          <font-awesome-icon icon="play"/>{{ $t('projects.recurring.run_round') }}
        </span>
        <span class="rp-hero__ghost" @click="$emit('open-tab', 'schedule')" qid="recurring-edit-schedule">
          <font-awesome-icon icon="calendar-alt"/>{{ project.scheduled ? $t('projects.recurring.edit_schedule') : $t('projects.recurring.set_schedule') }}
        </span>
        <slot name="actions"></slot>
      </div>
    </div>

    <div class="rp-next" :class="{'rp-next--due': project.run_due}">
      <div class="rp-ring">
        <svg viewBox="0 0 92 92">
          <circle cx="46"
                  cy="46"
                  r="38"
                  class="rp-ring__track"/>
          <circle cx="46"
                  cy="46"
                  r="38"
                  class="rp-ring__value"
                  :stroke-dasharray="circumference"
                  :stroke-dashoffset="circumference * (1 - ringFraction)"
                  transform="rotate(-90 46 46)"/>
        </svg>
        <b v-if="project.run_due">!<small>{{ $t('projects.management.due') }}</small></b>
        <b v-else-if="daysToNext !== null">{{ daysToNext }}<small>{{ $tc('projects.recurring.days', daysToNext) }}</small></b>
        <b v-else>—</b>
      </div>
      <div>
        <div class="rp-next__label">{{ $t('projects.recurring.next_round') }}</div>
        <div class="rp-next__date">{{ nextText }}</div>
        <div class="rp-next__text">{{ nextHelp }}</div>
      </div>
    </div>
  </div>
</template>

<script>
  import { typeMeta, nextRunDate } from '@/views/Projects/Management/projectTypes.js'

  const PERIOD_DAYS = {Days: 1, Months: 30.4, Years: 365};
  const PRESETS = {
    month: {every: 1, interval: 'Months'},
    quarter: {every: 3, interval: 'Months'},
    half_year: {every: 6, interval: 'Months'},
    year: {every: 1, interval: 'Years'}
  };

  // Hero of a recurring (continuous) project: schedule, automatic creation and a countdown to the next round (US 9396)
  export default {
    name: 'RecurringProjectHero',
    props: {
      project: {type: Object, required: true},
      canRun: {type: Boolean, default: false}
    },
    data: function() {
      return {
        circumference: 2 * Math.PI * 38
      }
    },
    computed: {
      meta() {
        return typeMeta(this.project.type);
      },
      nextRun() {
        return nextRunDate(this.project);
      },
      daysToNext() {
        var moment = require('moment');
        return this.nextRun ? this.nextRun.diff(moment().startOf('day'), 'days') : null;
      },
      ringFraction() {
        if (this.project.run_due) {
          return 1;
        }
        if (this.daysToNext === null) {
          return 0;
        }
        let period = (parseInt(this.project.repeat_after, 10) || 1) * (PERIOD_DAYS[this.project.repeat_after_interval] || 365);
        return Math.max(0.03, Math.min(1, 1 - this.daysToNext / period));
      },
      scheduleText() {
        var moment = require('moment');
        if (!this.project.scheduled || !this.project.repeat_after) {
          return this.$t('projects.launcher.schedule.none');
        }
        let every = parseInt(this.project.repeat_after, 10);
        let preset = _.findKey(PRESETS, {every: every, interval: this.project.repeat_after_interval});
        let interval = preset
          ? this.$t('projects.launcher.schedule.' + preset)
          : this.$t('projects.launcher.every') + ' ' + every + ' ' + this.$t('system.interval_' + (this.project.repeat_after_interval || '').toLowerCase()).toLowerCase();
        return this.$t('projects.recurring.from_date', {
          interval: interval,
          date: moment(this.project.start_date.substring(0, 10)).format('D MMM YYYY')
        });
      },
      nextText() {
        if (this.project.run_due) {
          return this.$t('projects.launcher.due_now');
        }
        return this.nextRun ? this.nextRun.format('D MMM YYYY') : this.$t('projects.launcher.schedule.none');
      },
      nextHelp() {
        if (this.project.run_due) {
          return this.$t('projects.recurring.due_help');
        }
        if (!this.nextRun) {
          return this.$t('projects.recurring.unscheduled_help');
        }
        return this.project.auto_create ? this.$t('projects.recurring.auto_help') : this.$t('projects.recurring.manual_help');
      }
    }
  }
</script>
